// src/components/MaterielMobilierManagement.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Plus, Edit, Trash2 } from 'lucide-react'; // Ajout d'icônes

// Fonction de calcul de l'annuité d'amortissement linéaire (simplifiée)
const calculateAnnualAmortization = (valeurBrute, dureeUtilite) => {
    const vBrute = parseFloat(valeurBrute);
    const duree = parseInt(dureeUtilite, 10);
    
    if (!vBrute || !duree || duree <= 0) return 0;
    
    // Annuité = Valeur Brute / Durée d'utilité
    const annualCharge = vBrute / duree;
    
    // Arrondi à deux décimales
    return parseFloat(annualCharge.toFixed(2));
};

const formatAmount = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount); 
};


const MaterielMobilierManagement = ({ db, schoolId, userRole, setMessage, setError }) => {
  const canManageMaterials = ['superAdmin', 'admin', 'financier'].includes(userRole);

  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    type: '', // Ex: Mobilier, Informatique, Didactique, Bureautique, Bâtiment (pour type immobilisation)
    quantity: 1,
    acquisitionDate: '',
    status: 'Fonctionnel', // Ex: Fonctionnel, En réparation, Hors service
    location: '', // Ex: Salle 1, Bureau Direction, Magasin
    // --- NOUVEAUX CHAMPS COMPTABLES POUR L'AMORTISSEMENT ---
    valeurBrute: '',        // Coût d'acquisition (Base amortissable)
    dureeUtilite: '',       // Durée d'amortissement en années (ex: 5)
    amortissementsCumules: 0, // Total des amortissements depuis l'acquisition (pour le Bilan)
    annuiteAmortissement: 0,  // Charge annuelle (pour le Compte de Résultat)
  });

  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Mise à jour de la référence Firestore : 'immobilisations' au lieu de 'materials'
  useEffect(() => {
    if (!db || !schoolId) return;

    setMessage('');
    setError('');

    const materialsCollectionRef = collection(db, 'schools', schoolId, 'immobilisations');
    const q = query(materialsCollectionRef, orderBy('acquisitionDate', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const materialsList = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        // Convertir les dates Firestore en format ISO string pour les champs input type="date"
        acquisitionDate: doc.data().acquisitionDate?.toDate ? 
                         doc.data().acquisitionDate.toDate().toISOString().split('T')[0] : 
                         doc.data().acquisitionDate || ''
      }));
      setMaterials(materialsList);
    }, (error) => {
      console.error("Erreur lors du chargement des immobilisations: ", error);
      setError("Erreur lors du chargement des données.");
    });

    return () => unsubscribe();
  }, [db, schoolId, setMessage, setError]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewMaterial(prev => ({ ...prev, [name]: value }));
  };

  const handleEditMaterial = (material) => {
    if (!canManageMaterials) return;
    
    // Remplir le formulaire avec les données de l'immobilisation
    setNewMaterial({
      ...material,
      // S'assurer que les champs numériques sont re-convertis pour l'édition
      valeurBrute: material.valeurBrute.toString(),
      dureeUtilite: material.dureeUtilite.toString(),
      amortissementsCumules: material.amortissementsCumules || 0, // Conserver la valeur existante
      annuiteAmortissement: material.annuiteAmortissement || 0,
    });
    setEditingMaterialId(material.id);
  };

  const handleAddOrUpdateMaterial = async (e) => {
    e.preventDefault();
    if (!canManageMaterials) return;

    const { name, type, valeurBrute, dureeUtilite, acquisitionDate } = newMaterial;

    if (!name || !valeurBrute || !dureeUtilite || !acquisitionDate) {
        setError("Veuillez remplir tous les champs obligatoires (Nom, Valeur Brute, Durée d'Utilité, Date d'acquisition).");
        return;
    }

    // Calcul de l'annuité d'amortissement
    const annuiteAmortissement = calculateAnnualAmortization(valeurBrute, dureeUtilite);

    const dataToSave = {
        ...newMaterial,
        schoolId: schoolId, // <--- C'EST LA LIGNE CLÉ À AJOUTER ICI !
        // Conversion des montants numériques
        valeurBrute: parseFloat(valeurBrute),
        dureeUtilite: parseInt(dureeUtilite, 10),
        annuiteAmortissement: annuiteAmortissement, // Annuité calculée
        // L'amortissement cumulé est 0 à l'acquisition, ou conservé lors d'une modification simple
        amortissementsCumules: editingMaterialId ? parseFloat(newMaterial.amortissementsCumules || 0) : 0, 
    };

    try {
      if (editingMaterialId) {
        // Mise à jour de l'immobilisation existante
        const docRef = doc(db, 'schools', schoolId, 'immobilisations', editingMaterialId);
        await updateDoc(docRef, dataToSave);
        setMessage("Immobilisation mise à jour avec succès.");
      } else {
        // Ajout d'une nouvelle immobilisation
        await addDoc(collection(db, 'schools', schoolId, 'immobilisations'), dataToSave);
        setMessage("Nouvelle immobilisation ajoutée avec succès.");
      }
      
      // Réinitialisation du formulaire
      setNewMaterial({
        name: '', type: '', quantity: 1, acquisitionDate: '', status: 'Fonctionnel', location: '',
        valeurBrute: '', dureeUtilite: '', amortissementsCumules: 0, annuiteAmortissement: 0,
      });
      setEditingMaterialId(null);
      setError('');

    } catch (e) {
      console.error("Erreur lors de la sauvegarde/mise à jour de l'immobilisation: ", e);
      setError(`Erreur lors de la sauvegarde: ${e.message}`);
    }
  };


  const handleDeleteMaterial = async (id) => {
    if (!canManageMaterials || !window.confirm("Êtes-vous sûr de vouloir supprimer cette immobilisation ? Cette action est irréversible.")) return;

    try {
      await deleteDoc(doc(db, 'schools', schoolId, 'immobilisations', id));
      setMessage("Immobilisation supprimée avec succès.");
    } catch (e) {
      console.error("Erreur lors de la suppression: ", e);
      setError(`Erreur lors de la suppression: ${e.message}`);
    }
  };

  const filteredMaterials = materials.filter(material => 
    (material.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (material.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-2">
        Gestion des Immobilisations (Actif Permanent)
      </h2>
      
      {!canManageMaterials && (
        <p className="text-red-500 mb-4">Vous n'avez pas la permission de gérer les immobilisations.</p>
      )}

      {/* Formulaire d'ajout/modification */}
      <div className="mb-8 p-6 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-xl font-medium text-gray-700 mb-4">
          {editingMaterialId ? 'Modifier l\'Immobilisation' : 'Ajouter une Nouvelle Immobilisation'}
        </h3>
        <form onSubmit={handleAddOrUpdateMaterial}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                
                {/* Nom / Description */}
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nom / Description</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={newMaterial.name}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>
                
                {/* Type */}
                <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type d'Immobilisation</label>
                    <select
                        id="type"
                        name="type"
                        value={newMaterial.type}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                        <option value="">Sélectionner</option>
                        <option value="corporel">Corporelle (Matériel, Bâtiment)</option>
                        <option value="incorporel">Incorporelle (Logiciel, Brevet)</option>
                    </select>
                </div>

                {/* Date d'Acquisition */}
                <div>
                    <label htmlFor="acquisitionDate" className="block text-sm font-medium text-gray-700">Date d'Acquisition</label>
                    <input
                        type="date"
                        id="acquisitionDate"
                        name="acquisitionDate"
                        value={newMaterial.acquisitionDate}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>
                
                {/* Quantité */}
                <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantité</label>
                    <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        value={newMaterial.quantity}
                        onChange={handleChange}
                        min="1"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>

                {/* --- NOUVEAUX CHAMPS COMPTABLES --- */}

                {/* Valeur Brute (Coût d'acquisition HT) */}
                <div>
                    <label htmlFor="valeurBrute" className="block text-sm font-medium text-gray-700">
                        Valeur Brute (F CFA HT)
                    </label>
                    <input
                        type="number"
                        id="valeurBrute"
                        name="valeurBrute"
                        value={newMaterial.valeurBrute}
                        onChange={handleChange}
                        required
                        step="0.01"
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>

                {/* Durée d'Utilité (en années) */}
                <div>
                    <label htmlFor="dureeUtilite" className="block text-sm font-medium text-gray-700">
                        Durée d'Utilité (Années)
                    </label>
                    <input
                        type="number"
                        id="dureeUtilite"
                        name="dureeUtilite"
                        value={newMaterial.dureeUtilite}
                        onChange={handleChange}
                        min="1"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>

                {/* Statut */}
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
                    <select
                        id="status"
                        name="status"
                        value={newMaterial.status}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                        <option value="Fonctionnel">Fonctionnel</option>
                        <option value="En réparation">En réparation</option>
                        <option value="Hors service">Hors service</option>
                    </select>
                </div>

                {/* Localisation */}
                <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700">Localisation</label>
                    <input
                        type="text"
                        id="location"
                        name="location"
                        value={newMaterial.location}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    type="submit"
                    className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    disabled={!canManageMaterials}
                >
                    {editingMaterialId ? <><Edit className="w-4 h-4 mr-2" /> Modifier l'Immobilisation</> : <><Plus className="w-4 h-4 mr-2" /> Ajouter l'Immobilisation</>}
                </button>
            </div>
        </form>
      </div>

      {/* Tableau des immobilisations */}
      <h3 className="text-xl font-medium text-gray-700 mb-4">Inventaire des Immobilisations</h3>
      
      <div className="mb-4">
        <input
          type="text"
          placeholder="Rechercher par nom ou localisation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border border-gray-300 rounded-md w-full md:w-1/3"
        />
      </div>

      {filteredMaterials.length === 0 ? (
        <p className="text-gray-500">Aucune immobilisation trouvée.</p>
      ) : (
        <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Acquisition</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée Utilité</th>
                {/* Colonnes Amortissement / Bilan */}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valeur Brute</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amort. Cumulés</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-bold">VNC</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annuité (Charge)</th>
                {/* Autres colonnes */}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localisation</th>
                <th scope="col" className="relative px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredMaterials.map((material) => {
                const valeurNetteComptable = (material.valeurBrute || 0) - (material.amortissementsCumules || 0);

                return (
                  <tr key={material.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{material.name}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{material.type}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {material.acquisitionDate || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{material.dureeUtilite} ans</td>
                    
                    {/* Colonnes Amortissement / Bilan */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 font-medium">
                        {formatAmount(material.valeurBrute)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-red-600">
                        ({formatAmount(material.amortissementsCumules)})
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-green-700">
                        {formatAmount(valeurNetteComptable)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {formatAmount(material.annuiteAmortissement)}
                    </td>
                    
                    {/* Autres colonnes */}
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{material.location}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <button
                            onClick={() => handleEditMaterial(material)}
                            className="text-blue-600 hover:text-blue-900 mr-4 disabled:opacity-50"
                            disabled={!canManageMaterials}
                        >
                            <Edit className="w-4 h-4 inline" /> Modifier
                        </button>
                        <button
                            onClick={() => handleDeleteMaterial(material.id)}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            disabled={!canManageMaterials}
                        >
                            <Trash2 className="w-4 h-4 inline" /> Supprimer
                        </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
        <p className="font-medium">
            **Important pour la Clôture:** Pour que le Bilan soit exact d'une année sur l'autre, vous devez prévoir un script de clôture d'exercice qui, à chaque fin d'année, met à jour chaque immobilisation en ajoutant l'`annuiteAmortissement` au champ `amortissementsCumules`.
        </p>
    </div>

    </div>
  );
};

export default MaterielMobilierManagement;